import { ReactElement, useState } from 'react';
import Modal from '@components/Modal';
import useNoodleApi from '@hooks/useNoodleApi';
import { useToast } from '@hooks';
import { MESSAGE_ATTACHMENT_REFERENCE_TYPES } from '@typings/api-models';
import * as tsClient from '@tsClient';
import AddUserWorkflowCollaboratorForm from '@components/AddUserWorkflowCollaboratorForm';
import ArrowCircleLeft from '@components/Icons/ArrowCircleLeft';
import AssignCollaboratorsToAttachmentForm, { Collaborator } from '@components/AssignCollaboratorsToAttachmentForm';

type Props = {
  attachmentType: MESSAGE_ATTACHMENT_REFERENCE_TYPES;
  attachmentReferenceId: string;
  collaborators: Collaborator[];
  userWorkflowId: string;
  onClose: () => void;
  onSubmit: () => void;
};

const AssignCollaboratorsToAttachmentModal = ({
  attachmentType,
  attachmentReferenceId,
  collaborators,
  userWorkflowId,
  onClose,
  onSubmit,
}: Props): ReactElement => {
  const [isAddCollaboratorForm, setIsAddCollaboratorForm] = useState<boolean>(false);
  const [isInvitingAndAssigningCollaborator, setIsInvitingAndAssigningCollaborator] = useState<boolean>(false);

  const {
    getData: addCollaboratorFn,
  } = useNoodleApi(tsClient.workflows.addUserWorkflowCollaborator, {
    toastOnError: true,
  });

  const {
    fetchingState: updatingDocumentRequestUserPermissions,
    getData: updateDocumentRequestUserPermissionsFn,
  } = useNoodleApi(tsClient.documentRequests.updateDocumentRequestUserPermissions, {
    toastOnError: true,
    toastSuccessMessage: () => [useToast.ToastTypeVariants.INFO, 'Assignments saved.'],
  });

  const {
    fetchingState: updatingCreditReportPermissions,
    getData: updateCreditReportPermissions,
  } = useNoodleApi(tsClient.creditReportsB2C.updateCreditReportPermissions, {
    toastOnError: true,
    toastSuccessMessage: () => [useToast.ToastTypeVariants.INFO, 'Assignments saved.'],
  });

  const {
    fetchingState: updatingFormRequestPermissions,
    getData: updateFormRequestPermissionsFn,
  } = useNoodleApi(tsClient.formRequests.updateFormRequestPermissions, {
    toastOnError: true,
    toastSuccessMessage: () => [useToast.ToastTypeVariants.INFO, 'Assignments saved.'],
  });

  const {
    fetchingState: updatingInvoicePermissions,
    getData: updateInvoicePermissions,
  } = useNoodleApi(tsClient.invoices.updateInvoicePermissions, {
    toastOnError: true,
    toastSuccessMessage: () => [useToast.ToastTypeVariants.INFO, 'Assignments saved.'],
  });

  const {
    fetchingState: updatingCustomTermsPermissions,
    getData: updateCustomTermsPermissions,
  } = useNoodleApi(tsClient.customTerms.updateCustomTermsPermissions, {
    toastOnError: true,
    toastSuccessMessage: () => [useToast.ToastTypeVariants.INFO, 'Assignments saved.'],
  });

  const {
    fetchingState: updatingBookingPermissions,
    getData: updateBookingPermissions,
  } = useNoodleApi(tsClient.scheduling.updateBookingPermissions, {
    toastOnError: true,
    toastSuccessMessage: () => [useToast.ToastTypeVariants.INFO, 'Assignments saved.'],
  });

  const { getData: getDocumentRequestUserPermissionsFn } = useNoodleApi(tsClient.documentRequests.getDocumentRequestUserPermissions);
  const { getData: getFormRequestPermissionsFn } = useNoodleApi(tsClient.formRequests.getFormRequestPermissions);
  const { getData: getInvoicePermissions } = useNoodleApi(tsClient.invoices.getInvoicePermissions);
  const { getData: getCustomTermsPermissions } = useNoodleApi(tsClient.customTerms.getCustomTermsPermissions);
  const { getData: getBookingPermissions } = useNoodleApi(tsClient.scheduling.getBookingPermissions);
  const { getData: getCreditReportPermissionsFn } = useNoodleApi(tsClient.creditReportsB2C.getCreditReportPermissions);

  const getAssignedCollaborators = async (): Promise<string[]> => {
    if (!attachmentType || !attachmentReferenceId) {
      return [];
    }
    if (attachmentType === MESSAGE_ATTACHMENT_REFERENCE_TYPES.DOCUMENT_REQUEST_USER) {
      const { data: documentRequestPermissions } = await getDocumentRequestUserPermissionsFn({ id: attachmentReferenceId });
      if (documentRequestPermissions) {
        const assignedUserIds = documentRequestPermissions.map((permission) => permission.personId);
        return assignedUserIds;
      }
    } else if (attachmentType === MESSAGE_ATTACHMENT_REFERENCE_TYPES.CREDIT_REPORT) {
      const { data: creditReportPermissions } = await getCreditReportPermissionsFn({ creditReportId: attachmentReferenceId });
      if (creditReportPermissions) {
        const assignedUserIds = creditReportPermissions.map((permission) => permission.personId);
        return assignedUserIds;
      }
    } else if (attachmentType === MESSAGE_ATTACHMENT_REFERENCE_TYPES.FORM_REQUEST) {
      const { data: formRequestPermissions } = await getFormRequestPermissionsFn({ formRequestId: attachmentReferenceId });
      if (formRequestPermissions) {
        const assignedUserIds = formRequestPermissions.map((permission) => permission.personId);
        return assignedUserIds;
      }
    } else if (attachmentType === MESSAGE_ATTACHMENT_REFERENCE_TYPES.INVOICE) {
      const { data: invoicePermissions } = await getInvoicePermissions({ invoiceId: attachmentReferenceId });
      if (invoicePermissions) {
        const assignedUserIds = invoicePermissions.map((permission) => permission.personId);
        return assignedUserIds;
      }
    } else if (attachmentType === MESSAGE_ATTACHMENT_REFERENCE_TYPES.CUSTOM_TERMS) {
      const { data: termsPermissions } = await getCustomTermsPermissions({ id: attachmentReferenceId });
      if (termsPermissions) {
        const assignedUserIds = termsPermissions.map((p) => p.personId);
        return assignedUserIds;
      }
    } else if (attachmentType === MESSAGE_ATTACHMENT_REFERENCE_TYPES.BOOKING) {
      const { data: bookingPermissions } = await getBookingPermissions({ id: attachmentReferenceId });
      if (bookingPermissions) {
        const assignedUserIds = bookingPermissions.map((p) => p.personId);
        return assignedUserIds;
      }
    }
    return [];
  };

  const handleCollaboratorAssignment = async (userIds: string[]): Promise<void> => {
    if (attachmentType === MESSAGE_ATTACHMENT_REFERENCE_TYPES.DOCUMENT_REQUEST_USER) {
      await updateDocumentRequestUserPermissionsFn({ id: attachmentReferenceId, userIds});
    } else if (attachmentType === MESSAGE_ATTACHMENT_REFERENCE_TYPES.FORM_REQUEST) {
      await updateFormRequestPermissionsFn({ formRequestId: attachmentReferenceId, userIds});
    } else if (attachmentType === MESSAGE_ATTACHMENT_REFERENCE_TYPES.INVOICE) {
      await updateInvoicePermissions({ invoiceId: attachmentReferenceId, userIds});
    } else if (attachmentType === MESSAGE_ATTACHMENT_REFERENCE_TYPES.CUSTOM_TERMS) {
      await updateCustomTermsPermissions({ id: attachmentReferenceId, userIds });
    } else if (attachmentType === MESSAGE_ATTACHMENT_REFERENCE_TYPES.BOOKING) {
      await updateBookingPermissions({ id: attachmentReferenceId, userIds });
    } else if (attachmentType === MESSAGE_ATTACHMENT_REFERENCE_TYPES.CREDIT_REPORT) {
      await updateCreditReportPermissions({ creditReportId: attachmentReferenceId, userIds });
    }
    onSubmit();
  };

  const handleInviteCollaboratorAndAssign = async (name: string, email: string): Promise<void> => {
    setIsInvitingAndAssigningCollaborator(true);
    const { data } = await addCollaboratorFn({
      email,
      name,
      sendEmail: false,
      userWorkflowId,
    });
    const existingCollaboratorIds = await getAssignedCollaborators();
    if (data?.id) {
      await handleCollaboratorAssignment([...existingCollaboratorIds, data.id]);
    }
  };

  const isAssigning = updatingDocumentRequestUserPermissions.isFetching || updatingFormRequestPermissions.isFetching
    || updatingCreditReportPermissions.isFetching || updatingInvoicePermissions.isFetching
    || updatingCustomTermsPermissions.isFetching || updatingBookingPermissions.isFetching;

  return (
    <Modal
      hideBottomInset
      title={isAddCollaboratorForm ? 'Invite and Assign Collaborator' : 'Assign Collaborators'}
      onClose={isAddCollaboratorForm ? () => setIsAddCollaboratorForm(false) : onClose}
      closeIcon={isAddCollaboratorForm ? <ArrowCircleLeft weight="fill" size={24} color='var(--color-gray-100)' /> : undefined}
    >
      {isAddCollaboratorForm
        ? (
          <AddUserWorkflowCollaboratorForm
            onSubmit={handleInviteCollaboratorAndAssign}
            isSubmitting={isInvitingAndAssigningCollaborator}
            isAssign
          />
        )
        : (
          <AssignCollaboratorsToAttachmentForm
            attachmentReferenceId={attachmentReferenceId}
            attachmentType={attachmentType}
            collaborators={collaborators}
            onInviteClick={() => setIsAddCollaboratorForm(true)}
            onSubmit={handleCollaboratorAssignment}
            isSubmitting={isAssigning}
          />
        )
      }
    </Modal>
  );
};

export default AssignCollaboratorsToAttachmentModal;
